/**
 * Speichertrennung für den Übernahme-Tab ("Als Nutzer anmelden").
 *
 * Supabase legt die Anmeldung im `localStorage` ab, und der ist über ALLE Tabs
 * derselbe. Ohne Trennung wirft eine Übernahme die Administratorin aus ihrem
 * eigenen Konto — in jedem Tab.
 *
 * `sessionStorage` ist dagegen pro Tab. Dieses Modul leitet deshalb im
 * Übernahme-Tab alle Supabase-Auth-Schlüssel (`sb-…`) vom `localStorage` in den
 * `sessionStorage` um. Alles andere (Ansichtseinstellungen, `lastLesson_…`)
 * bleibt im gemeinsamen `localStorage`, damit sich der Übernahme-Tab sonst wie
 * ein normaler Tab verhält.
 *
 * WICHTIG: Dieses Modul muss als ALLERERSTES importiert werden (erste Zeile in
 * `main.tsx`). ES-Module laufen in Importreihenfolge — steht der Import weiter
 * unten, ist der Supabase-Client schon gebaut und hat sich den echten
 * `localStorage` gemerkt. Das bricht lautlos: Es sieht alles normal aus, nur
 * fliegt der Admin aus seinem Konto.
 */

/** Merker im sessionStorage: Dieser Tab ist ein Übernahme-Tab. */
export const UEBERNAHME_MARKE = "musikonzept:uebernahme";

/** Pfad, auf dem eine Übernahme startet. */
const UEBERNAHME_PFAD = "/auth/impersonate-callback";

/** Auth-Schlüssel von Supabase — nur diese werden umgeleitet. */
const istAuthSchluessel = (key: string) => key.startsWith("sb-");

/**
 * Lovable-Vorschau: Dort brokert `previewAuthStorage.ts` die Sitzung per
 * postMessage an den Editor, statt den `localStorage` zu benutzen. Unsere
 * Schicht greift dann nicht, und eine Übernahme würde die Admin-Sitzung im
 * Editor überschreiben. Dieselben Zonen wie in der generierten Datei.
 */
const PREVIEW_ZONES = [
  "lovableproject.com",
  "lovableproject-dev.com",
  "lovable.app",
  "gpt-eng.com",
  "gptengineer.run",
];

function istPreviewBroker(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  const aufPreviewZone = PREVIEW_ZONES.some((z) => host === z || host.endsWith("." + z));
  const imRahmen = Boolean(window.parent && window.parent !== window);
  return aufPreviewZone && imRahmen;
}

/**
 * Ist dieser Tab ein Übernahme-Tab? Beim ersten Aufruf im frisch geöffneten Tab
 * entscheidet die Adresse, danach der Merker im sessionStorage — der überlebt
 * die Weiterleitung nach `/mein-bereich`.
 */
export function istUebernahmeTab(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.sessionStorage.getItem(UEBERNAHME_MARKE) === "1") return true;
    if (window.location.pathname.startsWith(UEBERNAHME_PFAD)) {
      window.sessionStorage.setItem(UEBERNAHME_MARKE, "1");
      return true;
    }
  } catch {
    // Kein sessionStorage (privater Modus mancher Browser) — dann keine
    // Trennung. Lieber keine Übernahme als eine, die still danebenliegt.
  }
  return false;
}

/** Wurde die Schicht tatsächlich installiert? */
let trennungAktiv = false;

/**
 * Greift die Trennung in diesem Tab wirklich? Der Übernahme-Callback bricht ab,
 * wenn das `false` ist — lieber keine Übernahme als eine ausgeloggte Admin.
 */
export function uebernahmeTrennungAktiv(): boolean {
  return trennungAktiv;
}

function schichteSpeicher() {
  const echt = window.localStorage;
  const sitzung = window.sessionStorage;

  const schicht: Storage = {
    // length und key() bleiben beim echten localStorage: Code, der über die
    // Schlüssel läuft (z. B. `lastLesson_…`), soll unverändert funktionieren.
    get length() {
      return echt.length;
    },
    key: (i: number) => echt.key(i),
    getItem: (k: string) => (istAuthSchluessel(k) ? sitzung.getItem(k) : echt.getItem(k)),
    setItem: (k: string, v: string) => {
      if (istAuthSchluessel(k)) sitzung.setItem(k, v);
      else echt.setItem(k, v);
    },
    removeItem: (k: string) => {
      if (istAuthSchluessel(k)) sitzung.removeItem(k);
      else echt.removeItem(k);
    },
    // clear() räumt NUR die Anmeldung dieses Tabs weg. Den gemeinsamen
    // localStorage zu leeren, würde die Einstellungen aller anderen Tabs
    // mitnehmen.
    clear: () => {
      const wegwerfen: string[] = [];
      for (let i = 0; i < sitzung.length; i++) {
        const k = sitzung.key(i);
        if (k && istAuthSchluessel(k)) wegwerfen.push(k);
      }
      wegwerfen.forEach((k) => sitzung.removeItem(k));
    },
  };

  Object.defineProperty(window, "localStorage", {
    configurable: true,
    get: () => schicht,
  });
}

if (typeof window !== "undefined" && istUebernahmeTab() && !istPreviewBroker()) {
  try {
    schichteSpeicher();
    trennungAktiv = true;
  } catch (e) {
    console.error("[uebernahme] Speicher liess sich nicht trennen", e);
    try {
      window.sessionStorage.removeItem(UEBERNAHME_MARKE);
    } catch {
      /* ignorieren */
    }
  }
}
